import React, { useEffect, useState } from "react";
import { apiConstants, postGeneric, postMultipart } from "../common/constants";
import { FormProvider, RHFFileField, RHFSearchableSelectField } from "../common/hook-form";
import { useForm } from "react-hook-form";
import {
    Box, Button, Card, CardContent, Grid, Typography,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Skeleton,
} from "@mui/material";
import { FieldLabel } from "../common/FilterBar";
import { LoadingButton } from "@mui/lab";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import DownloadIcon from "@mui/icons-material/Download";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { useSnackbar } from "notistack";
import Loader from "../common/Loader";
import PageTitle from "../components/PageTitle";
import DownloadSampleFile from "../components/DownloadSampleFile";
import { formatDate } from "../common/util";
import { useGetAllActiveMerchantsIdAndNameCachedQuery } from "../redux/commonApiCalls";

// ─── Shared table styles ──────────────────────────────────────────────────────

const thSx = {
    fontSize: '10px', fontWeight: 700, color: '#6b7280',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    bgcolor: '#fafafa', borderBottom: '2px solid #e0e0e0',
    py: 1.25, px: 2,
};
const tdSx = { px: 2, py: 1.5, borderBottom: '1px solid #f0f0f0', fontSize: '13px', color: '#1a1a2e', verticalAlign: 'middle' };

// ─── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
    const ok = status === 'COMPLETED';
    return (
        <Box component="span" sx={{
            display: 'inline-block', px: '8px', py: '3px', borderRadius: '5px',
            fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px',
            bgcolor: ok ? '#f0fdf4' : '#fffbeb',
            color: ok ? '#16a34a' : '#b45309',
            border: `1px solid ${ok ? '#bbf7d0' : '#fde68a'}`,
        }}>
            {ok ? 'Completed' : 'Stuck / Failed'}
        </Box>
    );
}

// ─── Section card ─────────────────────────────────────────────────────────────

function SectionCard({ title, subtitle, children }) {
    return (
        <Card elevation={0} sx={{ borderRadius: '10px', border: '1px solid #e8e8e8', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #f0f0f0' }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1a1a2e' }}>{title}</Typography>
                {subtitle && <Typography sx={{ fontSize: '12px', color: '#9ca3af', mt: '2px' }}>{subtitle}</Typography>}
            </Box>
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                {children}
            </CardContent>
        </Card>
    );
}

// ─── Skeleton rows ────────────────────────────────────────────────────────────

function SkeletonRows({ cols, rows = 5 }) {
    return Array.from({ length: rows }).map((_, i) => (
        <TableRow key={i}>
            {Array.from({ length: cols }).map((__, j) => (
                <TableCell key={j} sx={tdSx}><Skeleton variant="text" width="80%" /></TableCell>
            ))}
        </TableRow>
    ));
}

// ─── Component ────────────────────────────────────────────────────────────────

const DonwloadMISfromAWB = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [isUploadingFile, setIsUploadingFile] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(10);
    const [data, setData] = useState([]);
    const [reload, setReload] = useState(0);
    const { data: merchants = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery();

    const methodsBulkUpload = useForm({
        defaultValues: { orderFile: null, merchant: '' }
    });
    const { handleSubmit: handleSubmitBulkUpload, reset: resetForm, watch } = methodsBulkUpload;
    const merchantVal = watch('merchant');

    const onSubmitBulkUpload = async (formData) => {
        setIsUploadingFile(true);
        try {
            const form = new FormData();
            form.append("excelFile", formData.orderFile[0]);
            const res = await postMultipart(
                `${apiConstants.GET_MIS_FROM_AWB}?merchantOid=${formData.merchant}`,
                form
            );
            if (res.status === 200) {
                setReload(prev => prev + 1);
                enqueueSnackbar("File uploaded successfully!", { variant: "success" });
            } else {
                enqueueSnackbar(res.data || 'Upload failed', { variant: "error" });
            }
        } catch (err) {
            const errorMessage = err?.response?.data?.message || err.message;
            enqueueSnackbar(errorMessage || "Failed to process the file. Please try again.", { variant: "error" });
            console.error('Upload error:', err);
        } finally {
            setIsUploadingFile(false);
        }
    };

    useEffect(() => {
        const merchantID = merchants.find(val => val.id === merchantVal)?.merchantId;
        if (merchantID) {
            setIsLoading(true);
            const request = {
                collection: "bulk_orders_result",
                offset: page * sizePerPage,
                limit: sizePerPage,
                andfilter: [
                    { field: "type", operator: "eq", value: 'AWB_NUMBER_BULK_ORDER_REPORT' },
                    { field: "merchantId", operator: "eq", value: merchantID }
                ],
                sort: { field: "time", direction: "DESC" }
            };
            postGeneric(apiConstants.REPORT_GENERIC_SEARCH, request)
                .then((res) => { setData(res?.data?.data || []); })
                .finally(() => setIsLoading(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sizePerPage, page, reload, merchantVal]);

    const rows = data || [];
    const hasMerchant = !!merchantVal;

    return (
        <>
            {isUploadingFile && <Loader />}
            <PageTitle>Download MIS from AWB</PageTitle>

            {/* ── Upload form ───────────────────────────────────────────── */}
            <Box sx={{ mb: 2 }}>
                <SectionCard
                    title="Upload AWB File"
                    subtitle="Select a merchant and upload an Excel file with AWB numbers to generate MIS reports"
                >
                    <FormProvider methods={methodsBulkUpload} onSubmit={handleSubmitBulkUpload(onSubmitBulkUpload)}>
                        <Grid container spacing={2} alignItems="flex-end">
                            <Grid item xs={12} md={6}>
                                <FieldLabel>Merchant *</FieldLabel>
                                <RHFSearchableSelectField
                                    fullWidth name="merchant" label="" required
                                    options={merchants.map((val) => ({ value: val.id, label: val.name }))}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FieldLabel>Excel File *</FieldLabel>
                                <RHFFileField name="orderFile" fileType={["xlsx"]} required label="Click to upload Excel file" />
                            </Grid>
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <LoadingButton
                                        startIcon={<FileUploadIcon />}
                                        type="submit"
                                        loading={isUploadingFile}
                                        variant="contained"
                                        size="small"
                                        disableElevation
                                    >
                                        Upload
                                    </LoadingButton>
                                    
                                    <Button
                                        variant="cotained"
                                        size="small"
                                        color="error"
                                        disableElevation
                                        onClick={() => { resetForm(); setData([]); }}
                                    >
                                        Reset
                                    </Button>
                                    <DownloadSampleFile fileName="AWB_MIS_REPORT" label="Sample" excel="excel" size="small" />
                                </Box>
                            </Grid>
                        </Grid>
                    </FormProvider>
                </SectionCard>
            </Box>

            {/* ── Upload history ────────────────────────────────────────── */}
            <Card elevation={0} sx={{ borderRadius: '10px', border: '1px solid #e8e8e8', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1a1a2e' }}>Upload History</Typography>
                    {rows.length > 0 && (
                        <Box sx={{ fontSize: '11px', color: '#9ca3af', bgcolor: '#f3f4f6', px: '8px', py: '2px', borderRadius: '20px' }}>
                            {rows.length} record{rows.length !== 1 ? 's' : ''}
                        </Box>
                    )}
                </Box>

                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ ...thSx, minWidth: 40 }}>#</TableCell>
                                <TableCell sx={{ ...thSx, minWidth: 180 }}>File Name</TableCell>
                                <TableCell sx={{ ...thSx, minWidth: 160 }}>Uploaded By</TableCell>
                                <TableCell sx={{ ...thSx, minWidth: 120 }}>Status</TableCell>
                                <TableCell sx={{ ...thSx, minWidth: 200 }}>Order Type</TableCell>
                                <TableCell sx={{ ...thSx, minWidth: 130 }}>Upload Date</TableCell>
                                <TableCell sx={{ ...thSx, minWidth: 80, textAlign: 'center' }}>Total</TableCell>
                                <TableCell sx={{ ...thSx, minWidth: 100, textAlign: 'center' }}>Processed</TableCell>
                                <TableCell sx={{ ...thSx, minWidth: 110, textAlign: 'center' }}>Report</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <SkeletonRows cols={9} rows={sizePerPage} />
                            ) : rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} sx={{ border: 0, py: 0 }}>
                                        <Box sx={{
                                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                                            justifyContent: 'center', py: 8, gap: 1.5,
                                        }}>
                                            <Box sx={{
                                                width: 52, height: 52, borderRadius: '12px',
                                                bgcolor: '#e3f2fd', color: '#1565c0',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            }}>
                                                <InboxOutlinedIcon sx={{ fontSize: 28 }} />
                                            </Box>
                                            <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#1a1a2e' }}>
                                                {hasMerchant ? 'No upload history found' : 'Select a merchant to view history'}
                                            </Typography>
                                            <Typography sx={{ fontSize: '12px', color: '#9ca3af', textAlign: 'center', maxWidth: 280 }}>
                                                {hasMerchant
                                                    ? 'No AWB MIS uploads have been made for this merchant yet.'
                                                    : 'Choose a merchant from the upload form above to load their past AWB MIS uploads.'}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : rows.map((row, idx) => (
                                <TableRow
                                    key={row._id}
                                    sx={{
                                        bgcolor: idx % 2 !== 0 ? '#fafbfc' : '#fff',
                                        '&:hover': { bgcolor: '#f0f7ff' },
                                        '&:last-child td': { border: 0 },
                                    }}
                                >
                                    {/* # */}
                                    <TableCell sx={{ ...tdSx, color: '#9ca3af', width: 40, fontSize: '12px' }}>
                                        {page * sizePerPage + idx + 1}
                                    </TableCell>

                                    {/* File Name */}
                                    <TableCell sx={tdSx}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                            <InsertDriveFileOutlinedIcon sx={{ fontSize: 15, color: '#9ca3af', flexShrink: 0, mt: '2px' }} />
                                            <Box sx={{ fontWeight: 500, fontSize: '12.5px', color: '#1a1a2e', wordBreak: 'break-word' }}>
                                                {row?.fileName || '—'}
                                            </Box>
                                        </Box>
                                    </TableCell>

                                    {/* Uploaded By */}
                                    <TableCell sx={tdSx}>
                                        <Box sx={{ fontSize: '12.5px', color: '#374151', wordBreak: 'break-word' }}>{row?.modifiedByUser || '—'}</Box>
                                    </TableCell>

                                    {/* Status */}
                                    <TableCell sx={tdSx}>
                                        <StatusBadge status={row?.status} />
                                    </TableCell>

                                    {/* Order Type */}
                                    <TableCell sx={tdSx}>
                                        <Box sx={{
                                            display: 'inline-block', px: '7px', py: '2px', borderRadius: '4px',
                                            fontSize: '10px', fontWeight: 600, textTransform: 'uppercase',
                                            letterSpacing: '0.4px', bgcolor: '#f3f4f6', color: '#374151',
                                            wordBreak: 'break-word', lineHeight: 1.5,
                                        }}>
                                            {row?.type || '—'}
                                        </Box>
                                    </TableCell>

                                    {/* Upload Date */}
                                    <TableCell sx={{ ...tdSx, color: '#6b7280', fontSize: '12px' }}>
                                        {row?.time ? formatDate(row.time) : '—'}
                                    </TableCell>

                                    {/* Total */}
                                    <TableCell sx={{ ...tdSx, textAlign: 'center' }}>
                                        <Box sx={{ fontSize: '13px', fontWeight: 700, color: '#1a1a2e' }}>
                                            {row?.totalOrders ?? '—'}
                                        </Box>
                                    </TableCell>

                                    {/* Processed */}
                                    <TableCell sx={{ ...tdSx, textAlign: 'center' }}>
                                        <Box sx={{ fontSize: '13px', fontWeight: 700, color: '#16a34a' }}>
                                            {row?.processed ?? '—'}
                                        </Box>
                                    </TableCell>

                                    {/* Report */}
                                    <TableCell sx={{ ...tdSx, textAlign: 'center' }}>
                                        {row?.reportS3Url ? (
                                            <Box
                                                component="button"
                                                onClick={() => window.open(row.reportS3Url, '_blank')}
                                                sx={{
                                                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                                                    px: '9px', py: '4px', borderRadius: '5px',
                                                    fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                                                    bgcolor: '#e3f2fd', color: '#1565c0', border: '1px solid #bfdbfe',
                                                    transition: 'all 0.15s',
                                                    '&:hover': { bgcolor: '#1565c0', color: '#fff', borderColor: '#1565c0' },
                                                }}
                                            >
                                                <DownloadIcon sx={{ fontSize: 13 }} /> Download
                                            </Box>
                                        ) : (
                                            <Box sx={{ fontSize: '12px', color: '#d1d5db' }}>—</Box>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {rows.length > 0 && (
                    <TablePagination
                        component="div"
                        count={-1}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={sizePerPage}
                        onRowsPerPageChange={(e) => { setSizePerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        rowsPerPageOptions={[10, 25, 50]}
                        labelDisplayedRows={({ from, to }) => `${from}–${to}`}
                        sx={{ borderTop: '1px solid #f0f0f0', fontSize: '12px' }}
                    />
                )}
            </Card>
        </>
    );
};

export default DonwloadMISfromAWB;
